import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { useStore } from '../store/useStore';
import { getFolders, getFiles } from '../utils/api';
import styles from './FilePickerModal.module.css';

// Single-select Finder-style file picker. Mirrors the layout of the Files
// page (folder tree on the left, contents on the right) but in read-only
// mode — no upload, rename, delete, etc. The user navigates folders, picks
// a file, and the parent's onSelect receives the file row + its breadcrumb.
//
// Used by MarkupPopup to attach a file to a markup. Could be reused as a
// generic file picker elsewhere later.

export default function FilePickerModal({ onClose, onSelect, title = 'Pick a file' }) {
  const { project } = useStore();

  const [folders, setFolders]   = useState([]);
  const [files,   setFiles]     = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [selectedFileId,   setSelectedFileId]   = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [loading, setLoading]   = useState(true);

  // Load folder tree once.
  useEffect(() => {
    if (!project) return;
    let alive = true;
    getFolders(project.id)
      .then((list) => { if (alive) { setFolders(list); setLoading(false); } })
      .catch((err) => { if (alive) { toast.error(err.message); setLoading(false); } });
    return () => { alive = false; };
  }, [project?.id]);

  // Reload files whenever the active folder changes.
  useEffect(() => {
    if (!project) return;
    let alive = true;
    const params = { project_id: project.id, folder_id: selectedFolderId || 'root' };
    getFiles(params)
      .then((list) => { if (alive) setFiles(list); })
      .catch((err) => { if (alive) toast.error(err.message); });
    return () => { alive = false; };
  }, [project?.id, selectedFolderId]);

  // Tree helpers.
  const childrenOf = useMemo(() => {
    const map = new Map();
    for (const f of folders) {
      const key = f.parent_id || 'root';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(f);
    }
    return map;
  }, [folders]);

  const folderById = useMemo(() => {
    const m = new Map();
    for (const f of folders) m.set(f.id, f);
    return m;
  }, [folders]);

  const breadcrumb = useMemo(() => {
    if (!selectedFolderId) return [];
    const out = [];
    let cur = folderById.get(selectedFolderId);
    while (cur) {
      out.unshift(cur);
      cur = cur.parent_id ? folderById.get(cur.parent_id) : null;
    }
    return out;
  }, [selectedFolderId, folderById]);

  const subfolders = childrenOf.get(selectedFolderId || 'root') || [];
  const selectedFile = files.find((f) => f.id === selectedFileId) || null;

  function toggleExpanded(id) {
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function handleConfirm() {
    if (!selectedFile) return;
    onSelect?.(selectedFile);
  }

  return (
    <div className={styles.backdrop} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <span>{title}</span>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        <div className={styles.body}>
          {/* Tree pane */}
          <aside className={styles.tree}>
            <div className={styles.treeHeader}>FOLDERS</div>

            <button
              className={`${styles.rootRow} ${selectedFolderId === null ? styles.rowActive : ''}`}
              onClick={() => setSelectedFolderId(null)}
            >
              <FolderIcon />
              <span className={styles.rowName}>All Files</span>
            </button>

            <div className={styles.treeBody}>
              {loading && <div className={styles.empty}>Loading…</div>}
              {!loading && folders.length === 0 && (
                <div className={styles.empty}>No folders.</div>
              )}
              {(childrenOf.get('root') || []).map((f) => (
                <FolderNode
                  key={f.id}
                  folder={f}
                  depth={0}
                  childrenOf={childrenOf}
                  selectedId={selectedFolderId}
                  onSelect={setSelectedFolderId}
                  expanded={expanded}
                  onToggle={toggleExpanded}
                />
              ))}
            </div>
          </aside>

          {/* Contents pane */}
          <main className={styles.main}>
            <div className={styles.crumbs}>
              <button
                className={`${styles.crumb} ${selectedFolderId === null ? styles.crumbActive : ''}`}
                onClick={() => setSelectedFolderId(null)}
              >All Files</button>
              {breadcrumb.map((b, i) => (
                <React.Fragment key={b.id}>
                  <span className={styles.crumbSep}>/</span>
                  <button
                    className={`${styles.crumb} ${i === breadcrumb.length - 1 ? styles.crumbActive : ''}`}
                    onClick={() => setSelectedFolderId(b.id)}
                  >{b.name}</button>
                </React.Fragment>
              ))}
            </div>

            <div className={styles.listWrap}>
              {subfolders.length > 0 && (
                <div className={styles.section}>
                  <div className={styles.sectionLabel}>Folders</div>
                  <div className={styles.grid}>
                    {subfolders.map((f) => (
                      <button
                        key={f.id}
                        className={styles.folderCard}
                        onClick={() => setSelectedFolderId(f.id)}
                        onDoubleClick={() => setSelectedFolderId(f.id)}
                      >
                        <FolderIcon large />
                        <span className={styles.folderCardName}>{f.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {files.length === 0 && subfolders.length === 0 && (
                <div className={styles.empty}>Empty folder.</div>
              )}

              {files.length > 0 && (
                <div className={styles.section}>
                  <div className={styles.sectionLabel}>Files</div>
                  <div className={styles.fileList}>
                    {files.map((f) => (
                      <button
                        key={f.id}
                        className={`${styles.fileRow} ${selectedFileId === f.id ? styles.fileRowActive : ''}`}
                        onClick={() => setSelectedFileId(f.id)}
                        onDoubleClick={() => { setSelectedFileId(f.id); onSelect?.(f); }}
                      >
                        <FileIcon mime={f.mime_type} />
                        <div className={styles.fileNameStack}>
                          <span className={styles.fileName}>{f.name}</span>
                          <span className={styles.fileMeta}>
                            {formatSize(f.size)}
                            {f.uploaded_by && ` · ${f.uploaded_by}`}
                            {f.created_at && ` · ${format(new Date(f.created_at), 'MMM d, yyyy')}`}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>

        <div className={styles.footer}>
          <div className={styles.footerInfo}>
            {selectedFile
              ? <>Selected: <strong>{selectedFile.name}</strong></>
              : <>Pick a file from the contents pane</>}
          </div>
          <div className={styles.footerActions}>
            <button className={styles.btnSecondary} onClick={onClose}>Cancel</button>
            <button
              className={styles.btnPrimary}
              onClick={handleConfirm}
              disabled={!selectedFile}
            >Select</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FolderNode({ folder, depth, childrenOf, selectedId, onSelect, expanded, onToggle }) {
  const kids = childrenOf.get(folder.id) || [];
  const isOpen = expanded.has(folder.id);
  const isSelected = selectedId === folder.id;
  return (
    <div className={styles.nodeWrap}>
      <div className={`${styles.node} ${isSelected ? styles.rowActive : ''}`} style={{ paddingLeft: 8 + depth * 14 }}>
        <button className={styles.twisty} onClick={() => onToggle(folder.id)} aria-label={isOpen ? 'Collapse' : 'Expand'}>
          {kids.length > 0 ? (isOpen ? <ChevronDown /> : <ChevronRight />) : <span className={styles.twistyDash}>·</span>}
        </button>
        <button className={styles.nodeName} onClick={() => onSelect(folder.id)}>
          <FolderIcon />
          <span className={styles.rowName}>{folder.name}</span>
        </button>
      </div>
      {isOpen && kids.map((k) => (
        <FolderNode
          key={k.id}
          folder={k}
          depth={depth + 1}
          childrenOf={childrenOf}
          selectedId={selectedId}
          onSelect={onSelect}
          expanded={expanded}
          onToggle={onToggle}
        />
      ))}
    </div>
  );
}

function formatSize(bytes) {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

const FolderIcon = ({ large }) => (
  <svg width={large ? 28 : 14} height={large ? 28 : 14} viewBox="0 0 20 20" fill="none">
    <path d="M2 5.5a1.5 1.5 0 011.5-1.5h4l1.5 2h7.5A1.5 1.5 0 0118 7.5v7A1.5 1.5 0 0116.5 16h-13A1.5 1.5 0 012 14.5v-9z"
      fill="var(--accent-dim)" stroke="var(--accent)" strokeWidth="1.3"/>
  </svg>
);
const FileIcon = ({ mime }) => {
  let tint = 'var(--text-dim)';
  if (mime?.startsWith('image/')) tint = 'var(--green)';
  else if (mime === 'application/pdf') tint = 'var(--red)';
  else if (mime?.includes('word')) tint = 'var(--blue)';
  else if (mime?.includes('sheet') || mime?.includes('excel')) tint = 'var(--green)';
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
      <path d="M5 2h7l4 4v11a1 1 0 01-1 1H5a1 1 0 01-1-1V3a1 1 0 011-1z"
        stroke={tint} strokeWidth="1.4" strokeLinejoin="round"/>
      <path d="M12 2v4h4" stroke={tint} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
};
const ChevronDown = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);
const ChevronRight = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path d="M3.5 2L6.5 5L3.5 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);
